import type { ConsoleApi } from '@braidlabs/console';
import type { FragmentManifest } from '@braidlabs/gateway';
import type { OrgSummary, Role, Session } from './session.tsx';

/**
 * The control plane, as the console sees it.
 *
 * Same origin: CloudFront routes `/api/*` to the control-api behind the tenant-facing hostname,
 * and Vite proxies it in development. That is why there is no CORS configuration anywhere in this
 * repository, and no preflight on the request path.
 */
export function baseUrlFor(session: Session): string {
  return `/api/orgs/${session.orgId}`;
}

/**
 * The `ConsoleApi` the OSS components are handed.
 *
 * `headers` is a function rather than an object because the OSS client calls it before every
 * request — which is what will let phase 3 swap a pasted token for a refreshed session without
 * the components knowing anything changed.
 */
export function consoleApiFor(session: Session): ConsoleApi {
  return {
    baseUrl: baseUrlFor(session),
    apiPath: '/registry',
    headers: () => ({ Authorization: `Bearer ${session.token}` }),
  };
}

export class ApiCallError extends Error {
  readonly status: number;
  readonly code: string;
  /** The `details` from the control plane's one error shape — findings, field paths, a conflict. */
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiCallError';
    this.status = status;
    this.code = code;
    if (details !== undefined) this.details = details;
  }
}

/** Calls the control plane, turning its one error shape into one exception type. */
export async function call<T>(
  session: Session,
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const response = await fetch(`${baseUrlFor(session)}${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${session.token}`,
      ...(init.headers ?? {}),
    },
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();

  // Not every failure comes from the control plane. A proxy, a CloudFront error page, or a
  // gateway timeout answers with HTML, and parsing that as JSON would replace the real problem
  // ("502 Bad Gateway") with a syntax error about a `<` — the least useful message available.
  let body: { code?: string; error?: string } & Record<string, unknown> = {};
  let parsed = true;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      parsed = false;
    }
  }

  if (!response.ok) {
    throw new ApiCallError(
      response.status,
      parsed ? body.code ?? 'unknown' : 'non_json_response',
      parsed ? body.error ?? response.statusText : `HTTP ${response.status} from ${path}`,
      parsed ? body.details : undefined,
    );
  }
  if (!parsed) {
    throw new ApiCallError(response.status, 'non_json_response', `expected JSON from ${path}`);
  }
  return body as T;
}

/**
 * Verifies a credential before the console pretends to be signed in.
 *
 * Without this the console accepts anything: a typo'd token "succeeds" and then surfaces as an
 * unexplained 500 on whichever page happens to load first. `GET /me` answers both questions at
 * once — is this token good, and what may it do.
 */
export async function verifyCredential(orgId: string, token: string): Promise<{
  role: Role;
  org: { slug: string; name: string };
}> {
  const response = await fetch(`/api/orgs/${orgId}/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (response.status === 401 || response.status === 404) {
    // The control plane deliberately answers 404 for another org's token rather than 403, so
    // "wrong org" and "wrong token" are indistinguishable here — and saying so is honest.
    throw new ApiCallError(response.status, 'invalid_credential',
      'That organization id and token do not match an organization you can reach.');
  }
  if (!response.ok) {
    throw new ApiCallError(response.status, 'unreachable',
      `The control plane returned ${response.status}.`);
  }

  const body = await response.json();
  return { role: body.role, org: { slug: body.org.slug, name: body.org.name } };
}

/**
 * `GET /api/orgs` — where a freshly signed-in console session goes next.
 *
 * Sign-in yields a credential and no org id, while every other route lives under
 * `/api/orgs/{orgId}/`. An API token gets the one org it names, so the picker needs no second
 * code path for it.
 */
export async function listOrgs(token: string): Promise<OrgSummary[]> {
  const response = await fetch('/api/orgs', { headers: { Authorization: `Bearer ${token}` } });

  if (response.status === 401) {
    throw new ApiCallError(401, 'invalid_credential', 'That credential is no longer accepted. Sign in again.');
  }
  if (!response.ok) {
    throw new ApiCallError(response.status, 'unreachable', `The control plane returned ${response.status}.`);
  }

  const body = await response.json() as { items?: OrgSummary[] };
  return body.items ?? [];
}

// ---- the shapes the control plane returns ----------------------------------------------------

export interface FragmentDraft {
  fragmentId: string;
  manifest: FragmentManifest;
  previewEndpoint?: string;
  updatedAt: string;
}

export type ShellTemplate = 'static' | 'byo' | 'react-ssr' | 'angular-ssr';

export interface Shell {
  shellId: string;
  slug: string;
  /**
   * All four, because the control plane returns all four.
   *
   * This was `'static'` — a literal — while dev was serving `react-ssr`, `angular-ssr` and `byo`
   * shells, so the console's own type said three of its rows could not exist. `PUT /shells`
   * accepts only the first three; `byo` is set by `POST /shells/{id}/byo`, which fetches the
   * origin and checks it serves a shell document. Reading and writing are different sets here,
   * and the type is the reading one.
   */
  template: ShellTemplate;
  isDefault: boolean;
  /** For `byo`: the origin the shell document is fetched from. */
  byoOrigin?: string;
  config: {
    name: string;
    brand?: { color?: string; logoUrl?: string };
    nav: { label: string; href: string }[];
    mounts: { slot: string; src?: string; region: 'main' | 'sidebar' | 'header' }[];
  };
}

/**
 * The layout draft, as the console is allowed to see it (ADR 0005, option C).
 *
 * Text, findings, and a picture — never the document. `text` is opaque: the console displays it,
 * lets a person edit it, and posts it back. Every judgement about what it means arrives from the
 * server pre-rendered, which is why nothing below names a field of the format. `console.spec.ts`
 * guards that this file, and every other, stays that way.
 */
export interface LayoutFix {
  /** What the link says. Names the change, not the finding. */
  label: string;
  /** The complete replacement text. Posted back exactly like a keystroke. */
  text: string;
}

export interface DraftFinding {
  severity: 'error' | 'warning';
  code: string;
  message: string;
  path: string;
  fragmentIds: string[];
  hint?: string;
  fixes: LayoutFix[];
}

export type SkeletonTone = 'screen' | 'widget' | 'chrome' | 'group';

export interface SkeletonBox {
  label: string;
  tone: SkeletonTone;
  /** How a group lays out its children. Presentational — the server decides, the console draws. */
  arrangement?: 'row' | 'stack';
  children?: SkeletonBox[];
}

export interface SkeletonRegion {
  region: 'header' | 'sidebar' | 'main';
  /** `null` when the route does not use this region — drawn as an absence, not omitted. */
  box: SkeletonBox | null;
}

export interface SkeletonRoute {
  match: string;
  regions: SkeletonRegion[];
}

export interface LayoutSkeleton {
  routes: SkeletonRoute[];
}

export interface LayoutDraft {
  /** When the saved draft was last written. Shown, not enforced — saves are last-write-wins. */
  revision: string;
  text: string;
  findings: DraftFinding[];
  /** `null` when the text does not describe a document at all. */
  skeleton: LayoutSkeleton | null;
}

export interface DraftPreset {
  id: string;
  name: string;
  description: string;
  text: string;
}

export interface LayoutDraftResponse {
  /** `null` for a shell that has no layout yet. */
  draft: LayoutDraft | null;
  presets: DraftPreset[];
}

export interface AuditEntry {
  SK: string;
  actor: string;
  action: string;
  target: string;
  createdAt: string;
  before?: unknown;
  after?: unknown;
  ip?: string;
}

export interface Channel {
  name: string;
  snapshotId: string | null;
  version: number;
  /**
   * The targeting rules deciding who this channel reaches.
   *
   * Read here only to say *how many* there are — the console does not interpret one. Optional
   * because a channel written before phase 5 has no `rules` key at all, and an older org's `live`
   * row would otherwise read as malformed rather than as untargeted.
   */
  rules?: unknown[];
}

/**
 * A preview grant (phase 5 §1.6).
 *
 * `hash` is not a secret and is how a grant is revoked — the secret itself is shown once, at mint,
 * and is never returned again by anything.
 */
export interface PreviewGrant {
  grantId: string;
  hash: string;
  channel: string;
  createdBy: string;
  createdAt: string;
  /** Epoch seconds. */
  ttl: number;
  revokedAt?: string;
}

/** `GET /registry/head` — the snapshot currently serving traffic, or null before the first publish. */
export interface HeadState {
  id: string | null;
  snapshot: { id: string; createdAt: string; manifests: FragmentManifest[] } | null;
}

// ---- phase 3: federated identity --------------------------------------------------------------

export type IdpProvider = 'saml' | 'oidc';

export interface Idp {
  provider: IdpProvider;
  poolId: string;
  poolDomain: string;
  clientId: string;
  /** A Secrets Manager reference. The secret itself is never returned by any route. */
  secretArn?: string;
  metadataUrl?: string;
  groupMap: Record<string, Role>;
  status: 'pending' | 'active' | 'error';
  lastError?: string;
  updatedAt: string;
}

export interface IdpFinding {
  severity: 'error' | 'warning';
  code: string;
  message: string;
}

export interface SessionRow {
  sessionId: string;
  subject: string;
  email?: string;
  expiresAt: string;
  revokedAt?: string;
  userAgent?: string;
  ip?: string;
  createdAt: string;
}
